import React, { useContext, useEffect, useRef } from 'react';
import { AnsweringSubscriptionTag } from '@bb/domain/dist/types';
import {
  Avatar,
  message,
  Space,
  TableProps,
  Tag,
  Icon,
  EnhancedFilterDropdown,
  Tooltip,
} from 'components';
import { searchForAnsweringService } from 'api/UserController';
import ErrorReporter from '@app/errorReporter';
import { Button } from '@brokerbay/design-components';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';
import { addAgentToHistory } from 'api/RecentSearchHistoryController';
import axios from 'axios';
import { MLS_TYPE_LABEL_MAP } from 'components/UniversalSearch/constants';
import { CrossOrgAnsweringContext } from 'contexts';
import { CHAT_SESSION_STORAGE_KEYS } from 'utils';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import type {
  UserSearchForAnsweringServiceResponseBody,
  JsonObject,
} from '@/app-package';
import {
  addDatacy,
  addDatacyToHeader,
  formatDateStr,
  getSubscriptionTagTextAndColor,
} from './utils';
import SearchTable from './SearchTable';
import AnsweringSearchContext from './AnsweringSearchContext';
import { useSearchParams } from './hooks';

const DEFAULT_MLS_AGENT_SEARCH_QUERY: any = {
  mlsTypes: [],
  name: '',
  page: 0,
  size: 20,
};

const handleEsConstraint = (count: number) => (count > 10000 ? 10000 : count);

const AnsweringAgentTable: React.FC = () => {
  const [searchParam, setSearchParam] = useSearchParams<{
    mlsTypesFilter: string[];
    subscriptionTagsFilter: string[];
    sort: { field: string; direction: 'asc' | 'desc' };
  }>();
  const [searchResult, setSearchResult] =
    React.useState<JsonObject<UserSearchForAnsweringServiceResponseBody>>();
  const { answeringConfirm } = useContext(CrossOrgAnsweringContext);
  const {
    mlsFilterOptions,
    text: debouncedText,
    page,
    setPage,
    generateCsvDownload,
    setTotal,
    setInitialTotal,
    isMls,
    isLoading,
    setIsLoading,
    hasValidStripeMlsProductIdForOrg,
  } = useContext(AnsweringSearchContext);
  const [mlsTypesFilter, setMlsTypesFilter] = React.useState<string[]>(
    searchParam.mlsTypesFilter || [],
  );
  const [subscriptionTagsFilter, setSubscriptionTagsFilter] = React.useState<
    string[]
  >(searchParam.subscriptionTagsFilter || []);
  const [sort, setSort] = React.useState<{
    field: string;
    direction: 'asc' | 'desc' | undefined;
  }>({ ...searchParam.sort });
  const { xl } = useBreakpoint();
  const isRendered = React.useRef(false);
  const initialFetchCount = useRef(null);

  const MLS_AGENT_SEARCH_QUERY = {
    name: debouncedText,
    ...(!isMls && { fetchSubscriptionDate: 'true' }),
    mlsTypes: mlsTypesFilter,
    sortField: sort?.field ?? 'firstName',
    sortOrder: sort?.direction ?? 'asc',
    ...(hasValidStripeMlsProductIdForOrg
      ? {
          answeringServiceTypes: subscriptionTagsFilter as (
            | AnsweringServiceType
            | 'ns'
            | 'on'
          )[],
        }
      : subscriptionTagsFilter?.length === 1 && {
          answeringSubscribed: (subscriptionTagsFilter[0] === 'on').toString(),
        }),
    page,
    size: 20,
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        generateCsvDownload({
          page,
          input: debouncedText,
          sortField: sort?.field ?? 'firstName',
          sortDir: sort?.direction ?? 'asc',
          dataType: 'agent',
          ...(hasValidStripeMlsProductIdForOrg
            ? {
                answeringServiceTypes: subscriptionTagsFilter as (
                  | AnsweringServiceType
                  | 'ns'
                  | 'on'
                )[],
              }
            : subscriptionTagsFilter?.length === 1 && {
                answeringSubscribed: subscriptionTagsFilter[0] === 'on',
              }),
        });
        // Temp fix: If there are filters in MLS Agent table when page loads, call API twice, once to populate the table and get the count of agents with current filter
        // and a second time to get the count of total agents belonging to the MLS. TODO: Refactor BE function to fetch the total count of agents belonging to the MLS.

        if (
          isMls &&
          !initialFetchCount.current &&
          (mlsTypesFilter?.length || subscriptionTagsFilter?.length)
        ) {
          const [result, { count: initialCount }] = await Promise.all([
            searchForAnsweringService(MLS_AGENT_SEARCH_QUERY),
            searchForAnsweringService(DEFAULT_MLS_AGENT_SEARCH_QUERY),
          ]);
          initialFetchCount.current = handleEsConstraint(initialCount);
          setInitialTotal(handleEsConstraint(initialCount));
          setTotal(handleEsConstraint(result.count));
          setSearchResult(result);
        } else {
          const result = await searchForAnsweringService(
            MLS_AGENT_SEARCH_QUERY,
          );
          if (isMls && !initialFetchCount.current) {
            // Since ES fetches only the first 10000 results, initialize counts in agent table maxed out at 10000
            initialFetchCount.current = handleEsConstraint(result.count);
            setInitialTotal(handleEsConstraint(result.count));
          }
          setTotal(handleEsConstraint(result.count));
          setSearchResult(result);
        }
      } catch (e) {
        message.error('Error fetching data');
        ErrorReporter.error('Error fetching data', e);
      }
      setIsLoading(false);
      // Trigger dummy resize to trigger useTableResizer to fetch correct tableHeaderHeight after data loads
      window.dispatchEvent(new Event('resize'));
    })();
  }, [
    debouncedText,
    mlsTypesFilter,
    subscriptionTagsFilter,
    sort?.field,
    sort?.direction,
    page,
    hasValidStripeMlsProductIdForOrg,
  ]);

  useEffect(() => {
    if (!isRendered.current) {
      isRendered.current = true;
      return;
    }
    setPage(0);
    setSearchParam({ mlsTypesFilter, subscriptionTagsFilter, sort });
  }, [
    debouncedText,
    mlsTypesFilter,
    subscriptionTagsFilter,
    sort?.field,
    sort?.direction,
  ]);

  const columns: TableProps['columns'] = ([
    {
      width: 160,
      title: 'Agent',
      dataIndex: 'firstName',
      key: 'name',
      sorter: true,
      sortOrder: sort?.field === 'firstName' ? `${sort?.direction}end` : null,
      showSorterTooltip: true,
      render: (_, user) => (
        <Space align="center" size="middle">
          <Avatar user={user} />
          {`${user.firstName} ${user.lastName}`}
        </Space>
      ),
      onCell: addDatacy('agents-name'),
      onHeaderCell: addDatacyToHeader('agents-name'),
    },
    {
      width: 160,
      title: 'Brokerage',
      dataIndex: 'orgName',
      sorter: true,
      sortOrder: sort?.field === 'orgName' ? `${sort?.direction}end` : null,
      showSorterTooltip: true,
      render: (orgName: string) => (
        <Space align="center" size="middle">
          {orgName}
        </Space>
      ),
      onCell: addDatacy('agents-brokerage'),
      onHeaderCell: addDatacyToHeader('agents-brokerage'),
    },
    {
      width: 100,
      title: 'Mobile',
      dataIndex: 'phone',
      render: (mobile: string) => mobile,
      onCell: addDatacy('agents-mobile'),
      onHeaderCell: addDatacyToHeader('agents-mobile'),
    },
    !isMls && {
      width: 110,
      title: 'MLS',
      dataIndex: 'mlsTypes',
      filters: mlsFilterOptions,
      filteredValue: mlsTypesFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown
          {...props}
          okText="Apply"
          resetText="Clear"
          allowSearch
        />
      ),
      render: (mlsTypes: string[]) =>
        Array.isArray(mlsTypes) &&
        mlsTypes
          .map(mlsType => MLS_TYPE_LABEL_MAP[mlsType] ?? mlsType)
          .join(', '),
      onCell: addDatacy('agents-mls'),
      onHeaderCell: addDatacyToHeader('agents-mls'),
    },
    {
      width: hasValidStripeMlsProductIdForOrg
        ? (xl ? 135 : 170) * 0.95
        : (xl ? 100 : 140) * 0.85,
      title: !hasValidStripeMlsProductIdForOrg ? 'Concierge' : 'Subscription',
      dataIndex: 'subscriptionTag',
      filters: hasValidStripeMlsProductIdForOrg
        ? [
            {
              text: 'Live Concierge',
              value: AnsweringServiceType.LiveConciergePlus,
            },
            {
              text: 'Appt Desk Only',
              value: AnsweringServiceType.AppointmentDesk,
            },
            { text: 'No Subscription', value: 'ns' },
            { text: 'On', value: 'on' },
          ]
        : [
            { text: 'On', value: 'on' },
            { text: 'N/S', value: 'ns' },
          ],
      filteredValue: subscriptionTagsFilter,
      filterDropdown: props => (
        <EnhancedFilterDropdown {...props} okText="Apply" resetText="Clear" />
      ),
      render: (subscriptionTag: AnsweringSubscriptionTag) => {
        const { text, color } = getSubscriptionTagTextAndColor(
          subscriptionTag,
          hasValidStripeMlsProductIdForOrg,
        );
        return (
          <StyledSpace>
            <StyledTag color={color}>{text}</StyledTag>
          </StyledSpace>
        );
      },
      onCell: addDatacy('agents-concierge'),
      onHeaderCell: addDatacyToHeader('agents-concierge'),
    },
    !isMls && {
      width: 100,
      title: (
        <Tooltip
          placement="top"
          title="Date Subscribed information will only appear for US orgs with Subscribed status ON"
        >
          Date Subscribed
        </Tooltip>
      ),
      dataIndex: 'subscriptionDate',
      render: (subscriptionDate: Date) => formatDateStr(subscriptionDate),
      onCell: addDatacy('agents-date-subscribed'),
      onHeaderCell: addDatacyToHeader('agents-date-subscribed'),
    },
    {
      width: 140,
      title: '',
      dataIndex: 'id',
      render: (id: string, { subscriptionTag, chatDisabled }) => (
        <StyledRightAlign>
          <StyledButton
            type="default"
            icon={
              <Icon
                type="chatBubble"
                offset={[0, -1.5]}
                height={11.5}
                width={11.5}
                color={'black'}
              />
            }
            disabled={chatDisabled}
            onClick={event => {
              event.stopPropagation();
              answeringConfirm({ type: 'agent', subscriptionTag }, () => {
                addAgentToHistory(id);
                axios.post('/messaging/newConvo', { userIds: [id] });
              })();
            }}
            data-cy={`agent-chat-button-${id}`}
          >
            Chat
          </StyledButton>
          {(isMls ||
            (subscriptionTag as AnsweringSubscriptionTag) ===
              AnsweringSubscriptionTag.ON) && (
            <StyledButton
              icon={<Icon type="antdLoginOutlined" height={15} width={15} />}
              onClick={event => {
                event.stopPropagation();

                answeringConfirm({ type: 'agent', subscriptionTag }, () => {
                  CHAT_SESSION_STORAGE_KEYS.forEach(key =>
                    sessionStorage.removeItem(key),
                  );
                  window.location.pathname = `/answering/assume_user/${id}`;
                })();
              }}
              data-cy={`agent-assume-button-${id}`}
            >
              Assume
            </StyledButton>
          )}
        </StyledRightAlign>
      ),
      onCell: addDatacy('agents-chat'),
      onHeaderCell: addDatacyToHeader('agents-chat'),
    },
  ] as TableProps['columns'])!.filter(Boolean);

  return (
    <SearchTable
      columns={columns}
      dataSource={searchResult?.users ?? []}
      onChange={(pagination, filters, sorter) => {
        setMlsTypesFilter(filters.mlsTypes as string[]);
        setSubscriptionTagsFilter(filters.subscriptionTag as string[]);
        if ((sorter as any).field && (sorter as any).order) {
          setSort({
            field: (sorter as any).field,
            direction: (sorter as any).order === 'descend' ? 'desc' : 'asc',
          });
        } else {
          setSort(null);
        }
      }}
      dataCy={'agents-table'}
      loading={isLoading}
      totalItems={searchResult?.count}
    />
  );
};

export default AnsweringAgentTable;

const StyledRightAlign = styled.div`
  &:before {
    content: '';
  }
  display: flex;
  align-items: center;
  button + button {
    margin-left: 6px;
  }
`;

const StyledButton = styled(Button)`
  color: black;
  border-radius: 2px;
`;

const StyledTag = styled(Tag)`
  border-radius: 2px;
`;

const StyledSpace = styled(Space)`
  padding: 0 10px;
`;
